import { useState } from "react";
import {
  DUE_DATE_OPTIONS,
  presetRange,
  rangeText,
} from "src/components/ClientReviews/clientReviewUtils";

const EMPTY_FORM = { range: "any", from: "", to: "" };
// Applied filter in the shape FilteredChipNew shows as a chip ({ due_date: { chip_label, chip_value } }).
export const EMPTY_FILTER = { due_date: null };

/**
 * Client reviews "Due date" filter, Members list pattern: a drawer form (`form`), applied on
 * "Filter" (`applied`, shown as a chip), removed by the chip ✕ or "Clear All".
 * onApplied runs after every change (e.g. back to page 1).
 */
export default function useDueDateFilter(onApplied) {
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [applied, setApplied] = useState(EMPTY_FILTER);

  const apply = (next) => {
    setApplied(next);
    onApplied?.();
  };

  const open = () => {
    // The drawer starts from what is applied.
    const current = applied.due_date;
    setForm(current ? { range: current.chip_value, from: current.from, to: current.to } : EMPTY_FORM);
    setIsOpen(true);
  };
  const close = () => setIsOpen(false);

  const change = (value, name) => {
    setForm((old) => {
      const next = { ...old, [name]: value };
      // Custom starts from this month so the dates are not empty.
      if (name === "range" && value === "custom" && !old.from && !old.to) {
        Object.assign(next, presetRange("this_month"));
      }
      return next;
    });
  };

  const submit = () => {
    const type = form.range;
    const dates = type === "custom" ? { from: form.from, to: form.to } : presetRange(type);
    if (type === "any" || (!dates.from && !dates.to)) {
      apply(EMPTY_FILTER);
    } else {
      const label = DUE_DATE_OPTIONS.find((o) => o.value === type)?.label;
      apply({
        due_date: {
          chip_value: type,
          chip_label:
            type === "custom"
              ? `Due date: ${rangeText(dates)}`
              : `Due date: ${label} (${rangeText(dates)})`,
          ...dates,
        },
      });
    }
    close();
  };

  const removeChip = (next) => {
    setForm(EMPTY_FORM);
    apply({ ...EMPTY_FILTER, ...next });
  };

  const clear = () => {
    setForm(EMPTY_FORM);
    apply(EMPTY_FILTER);
    close();
  };

  return {
    isOpen,
    form,
    applied,
    range: applied.due_date
      ? { from: applied.due_date.from, to: applied.due_date.to }
      : { from: "", to: "" },
    open,
    close,
    change,
    submit,
    removeChip,
    clear,
  };
}
